/*
 * Reserva e fila persistentes do totem. Porte de firmware/nps_kiosk/nps_outbox.cpp.
 *
 * reserva (pool) — solicitacoes criadas enquanto o totem esta ocioso. Tira a
 *   ida a rede do caminho do toque e cobre quedas curtas de conexao.
 * fila (outbox) — o par (requestId, nota) e gravado ANTES do POST e so sai
 *   quando a API confirma.
 *
 * O blob inteiro e regravado a cada mudanca: sao ~1 KB e no maximo uma escrita
 * por toque. Um localStorage sincrono da conta; IndexedDB nao protegeria mais,
 * porque a opcao "Clear Web Storage" do Fully Kiosk apaga os dois.
 */

const STORAGE_KEY = "kfd.kiosk.v1";
const STORE_VERSION = 1;

const POOL_MAX = 16;
const OUTBOX_MAX = 32;

const emptyStore = (customer, surveyId) => ({
  version: STORE_VERSION,
  customer,
  surveyId: surveyId || "",
  question: "",
  pool: [],
  outbox: [],
});

/*
 * Blob de outra versao, de outra loja ou de outra pesquisa e descartado: essas
 * solicitacoes pertencem a configuracao anterior e enviariam notas para o
 * cliente errado.
 */
export function loadStore(customer, surveyId) {
  let parsed;
  try {
    parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
  } catch (err) {
    parsed = null;
  }

  if (!parsed || parsed.version !== STORE_VERSION) return emptyStore(customer, surveyId);
  if (parsed.customer !== customer) return emptyStore(customer, surveyId);
  if ((parsed.surveyId || "") !== (surveyId || "")) return emptyStore(customer, surveyId);

  return {
    ...emptyStore(customer, surveyId),
    question: typeof parsed.question === "string" ? parsed.question : "",
    pool: Array.isArray(parsed.pool) ? parsed.pool.slice(0, POOL_MAX) : [],
    outbox: Array.isArray(parsed.outbox) ? parsed.outbox.slice(0, OUTBOX_MAX) : [],
  };
}

export function saveStore(store) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (err) {
    /* Storage cheio ou bloqueado pelo WebView: o totem segue operando em
     * memoria. Perder a fila e ruim, travar a coleta seria pior. */
    console.warn("kiosk: nao foi possivel persistir o estado", err);
  }
}

export function poolPush(store, requestId) {
  if (!requestId || store.pool.length >= POOL_MAX) return store;

  return { ...store, pool: [...store.pool, requestId] };
}

export function poolPop(store) {
  if (!store.pool.length) return { store, requestId: "" };

  const [requestId, ...rest] = store.pool;
  return { store: { ...store, pool: rest }, requestId };
}

/*
 * requestId pode entrar vazio: nota tocada sem reserva e sem rede. O flush cria
 * a solicitacao depois e reancora a entrada.
 *
 * Devolve `enqueued` porque quem chama faz a contabilidade da ultima entrada
 * depois do POST: com a fila cheia essa entrada nao existe, e mexer nela
 * apagaria a nota de outra pessoa.
 */
export function outboxEnqueue(store, requestId, rating) {
  if (store.outbox.length >= OUTBOX_MAX) {
    console.warn("kiosk: fila cheia; nota descartada");
    return { store, enqueued: false };
  }

  return {
    store: { ...store, outbox: [...store.outbox, { requestId: requestId || "", rating }] },
    enqueued: true,
  };
}

export function outboxPeek(store) {
  return store.outbox.length ? store.outbox[0] : null;
}

export function outboxDropFirst(store) {
  return { ...store, outbox: store.outbox.slice(1) };
}

export function outboxRehomeFirst(store, requestId) {
  if (!store.outbox.length) return store;

  const [first, ...rest] = store.outbox;
  return { ...store, outbox: [{ ...first, requestId }, ...rest] };
}

export function outboxDropLast(store) {
  return { ...store, outbox: store.outbox.slice(0, -1) };
}

export function outboxRehomeLast(store, requestId) {
  if (!store.outbox.length) return store;

  const last = store.outbox.length - 1;
  return {
    ...store,
    outbox: store.outbox.map((entry, index) =>
      index === last ? { ...entry, requestId } : entry
    ),
  };
}

export function setQuestion(store, question) {
  if (!question || question === store.question) return store;

  return { ...store, question };
}

export { POOL_MAX, OUTBOX_MAX };
